import type { ShellNavSection } from "@/components/shell/types";
import type { LinkTab } from "@/components/ui/link-tabs";
import type { ModeratorCopy } from "./moderator-copy";

/**
 * The moderator menu: four entries. What waits on the moderator first (test
 * grading and payout reviews open from there), then audio validation, the
 * contracts on one page, and the rarer tools last.
 */
export function buildModeratorNav(lang: string, copy: ModeratorCopy, todoBadge?: number): ShellNavSection[] {
  const base = `/${lang}/moderator`;
  return [
    {
      key: "moderation",
      items: [
        {
          key: "todo",
          label: copy.todo,
          href: `${base}/todo`,
          icon: "inbox",
          match: [`${base}/get-paid`, `${base}/payouts`],
          badge: todoBadge,
          keywords: [copy.testCorrection, copy.payouts],
        },
        { key: "validateAudio", label: copy.validateAudio, href: `${base}/validate-audio`, icon: "volume2" },
        { key: "contracts", label: copy.contracts, href: `${base}/contracts`, icon: "fileText" },
        { key: "tools", label: copy.tools, href: `${base}/tools`, icon: "upload", match: [`${base}/excel-translations`] },
      ],
    },
  ];
}

/** Steps of a contract after the test, then the published contracts to hand out. */
export const CONTRACT_SECTIONS = ["draft", "sent", "viewed", "signed", "final_validated", "assign"] as const;
export type ContractSection = (typeof CONTRACT_SECTIONS)[number];

/** The sections the test-grading screen (useGetPaid) lists, as opposed to the contracts API. */
export const PIPELINE_SECTIONS: readonly ContractSection[] = ["draft", "sent", "viewed", "final_validated"];

export function contractSectionOf(params: URLSearchParams): ContractSection {
  const requested = params.get("section");
  return CONTRACT_SECTIONS.find((section) => section === requested) ?? "draft";
}

export function moderatorContractTabs(lang: string, labels: Record<ContractSection, string>): LinkTab[] {
  const base = `/${lang}/moderator/contracts`;
  const icons = { draft: "edit", sent: "send", viewed: "eye", signed: "checkCircle", final_validated: "checkCircle2", assign: "users" } as const;
  return CONTRACT_SECTIONS.map((section) => ({
    key: section,
    label: labels[section],
    href: `${base}?section=${section}`,
    icon: icons[section],
    // The bare contracts URL opens the first step.
    match: section === "draft" ? [base] : undefined,
  }));
}

export const TOOL_TABS = ["imports", "phrases", "annotate"] as const;
export type ToolTab = (typeof TOOL_TABS)[number];

export function toolTabOf(params: URLSearchParams): ToolTab {
  const requested = params.get("tab");
  return TOOL_TABS.find((tab) => tab === requested) ?? "imports";
}

export function moderatorToolTabs(lang: string, labels: Record<ToolTab, string>): LinkTab[] {
  const base = `/${lang}/moderator/tools`;
  const icons = { imports: "upload", phrases: "bookOpen", annotate: "languages" } as const;
  return TOOL_TABS.map((tab) => ({
    key: tab,
    label: labels[tab],
    href: `${base}?tab=${tab}`,
    icon: icons[tab],
    match: tab === "imports" ? [base] : undefined,
  }));
}

/** The test-grading URL every notification e-mail points to. */
export const testGradingPath = (lang: string) => `/${lang}/moderator/get-paid?tab=moderator&section=pending`;

/**
 * Where an old "/moderator?tab=..." link lands now that the panel is split
 * into pages. Unknown or missing tabs open the to-do page.
 */
export function legacyModeratorPath(lang: string, tab: string | null): string {
  const base = `/${lang}/moderator`;
  switch (tab) {
    case "imports":
      return `${base}/tools?tab=imports`;
    case "phrases":
      return `${base}/tools?tab=phrases`;
    case "numeration":
      return `${base}/pay`;
    case "test-validation":
      return testGradingPath(lang);
    case "payout-requests":
      return `${base}/payouts`;
    default:
      return `${base}/todo`;
  }
}

/**
 * The grading page only grades. A link to one of the contract steps it used
 * to show (section=draft, sent...) goes to the contracts page; a link with no
 * section gets the grading one. Null: the URL is already right.
 */
export function testGradingRedirect(lang: string, section: string | null): string | null {
  if (section === "pending") return null;
  if (section === "signed" || PIPELINE_SECTIONS.some((step) => step === section)) {
    return `/${lang}/moderator/contracts?section=${section}`;
  }
  return testGradingPath(lang);
}
